import { Component, Event, EventEmitter, Listen, Method, Prop, State, h } from '@stencil/core';
import { Address } from '../../globals/address';
import { SuccessData } from '../../globals/callbacks';
import { Checkout } from '../../globals/checkout';
import { SiteConfig } from '../../globals/site-config';
import { VCard } from '../../globals/vcard';
import { getDateMMYY } from '../../helpers/date-formatter';
import { fillInputValue } from '../../helpers/fill-input';
import { getAuthorizationToken } from '../../utils';
import { getFromLocalStorage, setInLocalStorage } from '../../utils/storage';
import { WidgetTranslations, ApplyOnlyApprovalTranslation } from '../../globals/translations';
import Bugsnag from '@bugsnag/js';
import { whenRecaptchaReady } from '../../helpers';

Bugsnag.start({
  apiKey: 'd56091a81c95a6e00b74f5912dc64514',
  enabledReleaseStages: ['production', 'staging', 'qa'],
});

@Component({
  tag: 'prog-connect',
  styleUrl: 'pc-widget.css',
  shadow: true,
})
export class PcWidget {
  applyOnly: boolean;
  private checkoutData: Checkout = new Checkout();

  dialogElement: HTMLDialogElement;

  @State() lowesAppMode: boolean;
  @State() showLowesFiller: boolean;
  @State() showLoader: boolean;
  @State() token: string;
  @State() vCard: VCard;

  configUIData: Record<string, unknown> & CustomUIConfig = {};

  @State() retailerConfig: Partial<SiteConfig> = {
    hideCobranding: false,
  };

  @Prop({ mutable: true }) launchFunction: () => void;
  @Prop() standaloneLaunch = false;
  @Prop({ mutable: true, reflect: true }) visible: boolean;
  @Prop({ mutable: true }) translations: Partial<WidgetTranslations>;

  @Event({ bubbles: true, composed: true }) updateCheckoutData: EventEmitter<Checkout>;

  componentWillLoad() {
    this.translations = window.ProgConnect.translations;
  }

  componentDidLoad() {
    whenRecaptchaReady(() => {
      if (this.standaloneLaunch && !this.visible) {
        this.launchPopup();
      }
    });
  }

  //This is a hack to support the Lowes Mobile Android App Keyboard issue(LMAAK) - See BAD-10008 for details
  @Listen('lowesFocus', { target: 'body' })
  lowesFocus() {
    this.showLowesFiller = true;
  }

  //This is a hack to support the Lowes Mobile Android App Keyboard issue(LMAAK) - See BAD-10008 for details
  @Listen('lowesBlur', { target: 'body' })
  lowesBlur() {
    this.showLowesFiller = false;
  }

  @Listen('autoFillPayment', { target: 'body' })
  autoFillPayment() {
    this.exitConfirm();
  }

  @Listen('exitPromptConfirm', { target: 'body' })
  exitConfirm() {
    this.visible = false;

    if (this.vCard) {
      this.autoFillCardInfo({ ...this.vCard });
      this.autoFillAddressInfo(this.vCard.billingAddress);
      this.checkoutData.callbacks.onSuccess?.({
        id: this.vCard.leaseId,
        deliveryConfirmationUrl: this.vCard.updateDeliveryDateUrl,
        orderConfirmationUrl: this.vCard.updateOrderIdUrl,
      } as SuccessData);
    } else {
      this.checkoutData.callbacks?.onCancel?.();
    }
    this.dialogElement.close();
    document.body.style.overflow = 'scroll';
  }

  @Listen('launchButtonClick', { target: 'body' })
  launchButtonClicked() {
    const checkoutData = window.ProgConnect.launchFunction();

    this.dialogElement.showModal();

    this.Checkout(checkoutData);
    document.body.style.overflow = 'hidden';
  }

  @Listen('vCardReceived', { target: 'body' })
  vCardReceived(event: CustomEvent) {
    this.vCard = event.detail;
  }

  launchPopup() {
    const checkoutData = window.ProgConnect.launchFunction();

    this.Checkout(checkoutData);
  }

  autoFillCardInfo(card: { number: string; expDate: string; cvv: string; name?: string; firstName?: string; lastName?: string }) {
    if (!this.checkoutData?.merchant) {
      return;
    }

    fillInputValue(this.checkoutData.merchant.siteConfig?.cardNumber, card.number);
    fillInputValue(this.checkoutData.merchant.siteConfig?.name, `${card.name}`);
    fillInputValue(this.checkoutData.merchant.siteConfig?.firstName, `${card.firstName}`);
    fillInputValue(this.checkoutData.merchant.siteConfig?.lastName, `${card.lastName}`);
    fillInputValue(this.checkoutData.merchant.siteConfig?.cvv, card.cvv);
    fillInputValue(this.checkoutData.merchant.siteConfig?.expiration, getDateMMYY(card.expDate));
  }

  autoFillAddressInfo(billingAddress: Address) {
    if (!this.checkoutData?.merchant) {
      return;
    }

    fillInputValue(this.checkoutData.merchant.siteConfig?.address, billingAddress.line1);
    fillInputValue(this.checkoutData.merchant.siteConfig?.address2, billingAddress.line2);
    fillInputValue(this.checkoutData.merchant.siteConfig?.city, billingAddress.city);
    fillInputValue(this.checkoutData.merchant.siteConfig?.state, billingAddress.state);
    fillInputValue(this.checkoutData.merchant.siteConfig?.zip, billingAddress.zip);
  }

  @Method() async Checkout(checkout: Checkout): Promise<void> {
    this.showLoader = true;
    this.visible = true;

    const {
      authToken,
      parentStoreId,
      settings: { displayName, disableCobranding, logoUrl, minLeaseAmount, nonLeasableLimit, ui, leaseProhibitedInStates, overrideLeaseTermMonths, overrideBuyoutDays, features },
    } = await getAuthorizationToken(checkout.merchant.key);

    setInLocalStorage('progConnectAuthToken', authToken);

    this.retailerConfig = {
      ...this.retailerConfig,
      merchantDisplayName: displayName,
      merchantLogoUrl: logoUrl,
      hideCobranding: disableCobranding,
      parentStoreId,
      minLeaseAmount,
      nonLeasableLimit,
      leaseProhibitedInStates,
      overrideLeaseTermMonths,
      overrideBuyoutDays,
      features,
    };
    this.configUIData = ui || {};
    this.showLoader = false;
    this.checkoutData = checkout;
    this.applyOnly = checkout.merchant?.flowType === 'applyOnly';
    this.loadCustomText();
    this.checkForLowesApp();
  }

  loadCustomText() {
    if (this.configUIData && this.configUIData.ApplyOnlyApproval) {
      if (!this.translations) {
        this.translations = {};
      }

      if (!this.translations.applyFlow) {
        this.translations.applyFlow = {};
      }
      this.translations.applyFlow = {
        ...this.translations.applyFlow,
        applyOnlyApproval: this.configUIData.ApplyOnlyApproval as Partial<ApplyOnlyApprovalTranslation>,
      };
    }
  }

  getUserAgent(): string {
    const userAgent: string = navigator.userAgent || navigator.vendor || (window as any).opera || '';

    return userAgent.toLowerCase();
  }
  //This is a hack to support the Lowes Mobile Android App Keyboard issue(LMAAK) - See BAD-10008 for details
  //When ready to test out the filtering, add a return to the end of each this.lowesAppmode=false statement
  //This will enable filtering for lowes feature flag, on android
  checkForLowesApp() {
    this.lowesAppMode = false;

    //eslint-disable-next-line dot-notation
    if (Object.keys(this.configUIData).length === 0 || !('lowesHotfix' in this.configUIData) || this.configUIData['lowesHotfix'] === 'false') {
      return;
    }

    //Check that we are on an android device
    const userAgent = this.getUserAgent();

    if (!userAgent.includes('android')) {
      return;
    }

    //Final check, look to see if we are in a web view
    if (!userAgent.includes('wv')) {
      if (this.configUIData.lowesHotfix !== 'bypass') {
        return;
      }
    }

    this.lowesAppMode = true;
  }

  //This is a hack to support the Lowes Mobile Android App Keyboard issue(LMAAK) - See BAD-10008 for details
  getFillerDivMode() {
    if (!this.lowesAppMode) {
      return '';
    }

    return 'filler-div';
  }

  getModalclass() {
    let modalClass = 'modal';

    if (this.applyOnly) {
      modalClass += ' applyOnly';
    }

    if (this.lowesAppMode) {
      modalClass += ' lowesMode';
    }

    return modalClass;
  }
  render() {
    return (
      <div>
        {!this.standaloneLaunch ? (
          <div>
            {this.vCard ? (
              <pc-vcard-checkout merchantDisplayName={this.retailerConfig.merchantDisplayName} card={this.vCard} configUIData={this.configUIData} />
            ) : (
              <pc-launch-button loading={this.showLoader} translation={this.translations?.launchButton}></pc-launch-button>
            )}
          </div>
        ) : null}
        <dialog class={this.visible ? 'pc-wrapper visible' : 'pc-wrapper'} ref={el => (this.dialogElement = el)}>
          <div class={this.getModalclass()}>
            {this.showLoader || getFromLocalStorage('progConnectAuthToken') === 'undefined' ? (
              <pc-loader-bar />
            ) : (
              <pc-apply-flow checkoutData={this.checkoutData} retailerConfig={this.retailerConfig} translations={this.translations?.applyFlow} configUIData={this.configUIData} />
            )}
            <div class={this.showLowesFiller && this.lowesAppMode ? 'lowes-filler-div' : ''} />
          </div>
        </dialog>
      </div>
    );
  }
}
